// @flow

import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';
import type { Subscription, Line } from 'digicel-kaizen-library';
import { isLoadingSelector, hasErrorSelector as hasLoadingErrorSelector } from '../LoadingStatus';
import type { StateType as GlobalState } from '../reducers';

export const isFetchingSelector = (state: GlobalState): boolean => isLoadingSelector(state, 'fetchAllSubscriptions');
export const hasErrorSelector = (state: GlobalState): boolean =>
  hasLoadingErrorSelector(state, 'fetchAllSubscriptions');

export const subscriptionsSelector = createSelector(
  (state: GlobalState) => state.subscriptions.subscriptionsByOuid,
  subscriptions => sortBy(subscriptions, ['line.rank'])
);

export const isSubscriptionActive = ({ status }: Subscription) => status === 'ACTIVE';
export const isSubscriptionSuspended = ({ status }: Subscription) => status === 'SUSPENDED';
export const isSubscriptionPendingActive = ({ status }: Subscription) => status === 'PENDING_ACTIVE';
export const isSubscriptionVisible = (subscription: Subscription) =>
  isSubscriptionActive(subscription) ||
  isSubscriptionSuspended(subscription) ||
  isSubscriptionPendingActive(subscription);

export const activeSubscriptionsSelector = (state: GlobalState) =>
  subscriptionsSelector(state).filter(subscription => isSubscriptionActive(subscription));

export const suspendedSubscriptionsSelector = (state: GlobalState) =>
  subscriptionsSelector(state).filter(subscription => isSubscriptionSuspended(subscription));

export const pendingActiveSubscriptionsSelector = (state: GlobalState) =>
  subscriptionsSelector(state).filter(subscription => isSubscriptionPendingActive(subscription));

export const hasActiveSubscriptions = (state: GlobalState): boolean => activeSubscriptionsSelector(state).length !== 0;
export const hasSuspendedSubscriptions = (state: GlobalState): boolean =>
  suspendedSubscriptionsSelector(state).length !== 0;

export const phoneLinesSelector = (state: GlobalState): Array<Line> =>
  activeSubscriptionsSelector(state).map(subscription => subscription.line);

export const isSubscriptionToActivate = (subscription: Subscription) =>
  subscription.status === 'CREATED' ||
  (subscription.status === 'PENDING_ACTIVE' && !!subscription?.portability?.characteristics);

export const isSubscriptionCreatedWithoutLineNumber = (subscription: Subscription) =>
  subscription.status === 'CREATED' && !subscription?.line?.number;

const hasSubscriptionOptions = ({ options }: Subscription) => !!options.length;
const hasSubscriptionNoOptions = ({ options }: Subscription) => !options.length;

export const visibleSubscriptionsToActivateSelector = (state: GlobalState) => {
  return [
    ...subscriptionsToActivateSelector(state).filter(hasSubscriptionOptions),
    subscriptionsToActivateSelector(state).find(hasSubscriptionNoOptions),
  ].filter(Boolean);
};

// withInProgress boolean is for some rare cases where you will want to show subscriptions that have an orderState IN_PROGRESS (such as on the Your Orders page)
export const subscriptionsToActivateSelector = (state: GlobalState, withInProgress?: boolean) => {
  const subscriptions = subscriptionsSelector(state).filter(
    subscription => isSubscriptionToActivate(subscription) && !isSubscriptionCreatedWithoutLineNumber(subscription)
  );

  return withInProgress
    ? subscriptions
    : subscriptions.filter(subscription => subscription.orderState !== 'IN_PROGRESS');
};

export const subscriptionsToActivateWithPlanSelector = (state: GlobalState) => {
  const subscriptions = subscriptionsToActivateSelector(state, true);
  return subscriptions.filter(({ line, portability }) => line?.number || portability?.characteristics?.MSISDN);
};

export const hasSubscriptionsToActivateWithoutOptionsSelector = (state: GlobalState) => {
  const subscriptions = subscriptionsToActivateSelector(state);
  return subscriptions.find(hasSubscriptionNoOptions);
};

export const shouldChooseAPlanForActivationSelector = (state: GlobalState): boolean => {
  const subscriptions = subscriptionsSelector(state);
  return subscriptions.some(({ options, status }) => !!options.length || status !== 'CREATED');
};

export const shouldShowPendingOrderPanelSelector = (state: GlobalState): boolean => {
  return !!subscriptionsToActivateWithPlanSelector(state).length;
};

export const subscriptionsByOuidSelector = ({ subscriptions }: GlobalState) => subscriptions.subscriptionsByOuid;

export const getSubscriptionToEditOuidSelector = (state: GlobalState) => state.subscriptions.subscriptionToUpdateOuid;

export const lastPendingSubscriptionSelector = (state: GlobalState) => {
  const subscriptionsByOuid = subscriptionsByOuidSelector(state);
  if (!subscriptionsByOuid) return null;
  const createdSubscription = Object.values(subscriptionsByOuid).find(
    // $FlowFixMe
    subscription => subscription.status === 'CREATED'
  );
  return createdSubscription ? createdSubscription : null;
};

export const subscriptionByOuidSelector = (state: GlobalState, ouid: ?string): ?Subscription => {
  const subscriptionsByOuid = subscriptionsByOuidSelector(state);
  if (!subscriptionsByOuid) return null;

  return subscriptionsByOuid[ouid];
};

export const subscriptionBySerialNumberSelector = (state: GlobalState, serialNumber: string): ?Subscription => {
  const subscriptionsByOuid = subscriptionsByOuidSelector(state);
  if (!subscriptionsByOuid) return null;

  const subscriptionKey = Object.keys(subscriptionsByOuid).filter(
    key => subscriptionsByOuid[key].subscriptionProductSerialNumber === serialNumber
  );

  return subscriptionsByOuid[subscriptionKey];
};
